import { isNil } from '@sixfold/typed-primitives';

export const BACKOFFICE_SESSION_OPERATION = 'ViewerBackofficeRole';

export type BackofficeSessionState = 'authenticated' | 'clear-session' | 'unauthenticated';

export type SessionFailureAction = 'login' | 'logout';

type BackofficeViewer = { backoffice_access_enabled: boolean | null } | null | undefined;

type GraphQLErrorLike = {
  message?: string;
  extensions?: { code?: unknown };
};

export function getBackofficeSessionState(viewer: BackofficeViewer): BackofficeSessionState {
  if (isNil(viewer)) {
    return 'unauthenticated';
  }

  if (viewer.backoffice_access_enabled === true) {
    return 'authenticated';
  }

  return 'clear-session';
}

export function responseSessionAction(input: {
  operationName?: string;
  statusCode?: number;
  graphQLErrors?: readonly GraphQLErrorLike[];
}): SessionFailureAction | undefined {
  const errors = input.graphQLErrors ?? [];
  const tokenRejected =
    input.statusCode === 401 ||
    errors.some((error) => error.message === 'Authentication Error' || error.extensions?.code === 'UNAUTHENTICATED');

  if (tokenRejected) {
    return 'login';
  }

  const sessionIsNotAuthorized =
    input.operationName === BACKOFFICE_SESSION_OPERATION && errors.some((error) => error.message === 'Not authorized');

  if (sessionIsNotAuthorized) {
    return 'logout';
  }

  return undefined;
}

export function sessionStateForQueryError(error: unknown): 'clear-session' | 'unauthenticated' {
  const action = responseSessionAction({
    operationName: BACKOFFICE_SESSION_OPERATION,
    statusCode: networkStatusCode(error),
    graphQLErrors: graphQLErrorsFrom(error),
  });

  if (action === 'logout') {
    return 'clear-session';
  }

  return 'unauthenticated';
}

function graphQLErrorsFrom(error: unknown): GraphQLErrorLike[] {
  if (!isRecord(error)) {
    return [];
  }

  const { graphQLErrors, message } = error;
  const fromList = Array.isArray(graphQLErrors) ? graphQLErrors.filter(isGraphQLErrorLike) : [];
  if (fromList.length > 0) {
    return fromList;
  }

  if (typeof message === 'string') {
    return [{ message }];
  }

  return [];
}

function networkStatusCode(error: unknown): number | undefined {
  if (!isRecord(error) || !isRecord(error.networkError)) {
    return undefined;
  }

  const { statusCode } = error.networkError;
  return typeof statusCode === 'number' ? statusCode : undefined;
}

function isGraphQLErrorLike(value: unknown): value is GraphQLErrorLike {
  return isRecord(value);
}

function isRecord(value: unknown): value is { [key: string]: unknown } {
  return typeof value === 'object' && value !== null;
}
